import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useToastStore, type ToastDescriptor } from './toastStore';

interface ToastItemProps {
  toast: ToastDescriptor;
}

function ToastItem({ toast }: ToastItemProps) {
  const removeToast = useToastStore((state) => state.removeToast);
  const { t } = useTranslation();

  const toneClassName =
    toast.tone === 'success'
      ? 'border-ai-primary/40 bg-ai-surface-elevated/90 text-ai-primary shadow-ai-card'
      : toast.tone === 'warning'
      ? 'border-ai-warning/50 bg-ai-surface-elevated/90 text-ai-warning shadow-ai-card'
      : toast.tone === 'danger'
      ? 'border-ai-danger/50 bg-ai-surface-elevated/90 text-ai-danger shadow-ai-card'
      : 'border-ai-primary/40 bg-ai-surface-elevated/90 text-ai-text-primary shadow-ai-card';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.16, ease: 'easeOut' } }}
      exit={{ opacity: 0, y: -12, scale: 0.9, transition: { duration: 0.15, ease: 'easeIn' } }}
      role="status"
      className={clsx(
        'pointer-events-auto w-full max-w-md rounded-2xl border px-4 py-3 text-sm backdrop-blur-xl',
        toneClassName
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 whitespace-pre-line">{toast.message}</div>
        <button
          type="button"
          className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border border-ai-surface-glow/40 text-[11px] font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/50 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
          onClick={() => removeToast(toast.id)}
          aria-label={t('app.notifications.dismiss')}
        >
          ×
        </button>
      </div>
    </motion.div>
  );
}

export function ToastStack() {
  const toasts = useToastStore((state) => state.toasts);

  if (!toasts.length) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-6 z-[2200] flex justify-center px-4 sm:top-8">
      <div className="flex w-full flex-col items-center gap-3 sm:gap-4">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
